import { useEffect, useMemo, useState } from "react";
import { GeoJSON, MapContainer, TileLayer } from "react-leaflet";
import { http } from "../../lib/api";
import { money } from "../../lib/casePath";
import { Card } from "../kit/Kit";
import { ATTR, FitTo, Legend, TILE, colorIdx } from "./geo";

const Bar = ({ r, lim }) => {
  const [open, setOpen] = useState(false);
  const tip = r.original && r.original !== r.k ? `${r.k}\nOriginal: ${r.original}` : r.k;
  return (
    <div className="min-w-0">
      <button type="button" onClick={() => setOpen(!open)} title={tip} className={`block w-full min-w-0 break-words text-left text-sm leading-snug ${open ? "" : "line-clamp-2"}`} data-testid={`bar-label-${String(r.original || r.k).replace(/\W+/g, "-").slice(0, 40)}`}>{r.k}</button>
      {open && r.original && r.original !== r.k && <p className="break-words text-[11px] text-[#8C867A]">Original: {r.original}</p>}
      <div className="relative mx-1 mt-1 h-2 rounded-full bg-[#F2F0EC]"><div className={`h-full max-w-full rounded-full ${lim && r.pct_cartera > lim ? "bg-rose-600" : "bg-[#E8643C]"}`} style={{ width: `${Math.min(100, r.pct_cartera * (100 / Math.max(lim || 50, 1)) / 2)}%` }} />
        {lim && <span className="absolute -top-1 h-4 w-0.5 -translate-x-1/2 bg-[#111]" style={{ left: "50%" }} title={`Límite ${lim}%`} />}</div>
      <div className="mt-0.5 flex flex-wrap justify-between gap-x-2 text-[11px] text-[#8C867A]"><span className="tabular-nums">{money(r.monto)}</span><span className="tabular-nums font-semibold text-[#111]">{r.pct_cartera.toFixed(1)}%</span></div>
    </div>
  );
};

const Bars = ({ rows, lim, testid }) => (
  <div className="min-w-0 space-y-3" data-testid={testid}>{rows.map((r) => <Bar key={r.k} r={r} lim={lim} />)}
    {!rows.length && <p className="text-sm text-[#8C867A]">Sin cartera dispuesta.</p>}</div>);

export const Portafolio = () => {
  const [d, setD] = useState(null);
  const [geo, setGeo] = useState(null);
  useEffect(() => { http.get("/riesgo/portafolio").then((r) => setD(r.data)); http.get("/riesgo/geo/estados").then((r) => setGeo(r.data)); }, []);
  const byEst = useMemo(() => Object.fromEntries((d?.por_estado || []).map((r) => [r.k, r])), [d]);
  if (!d || !geo) return <Card><p className="text-sm text-[#8C867A]">Calculando portafolio…</p></Card>;
  const pct = (n) => byEst[n]?.pct_cartera;
  const alias = { "México": "Estado de México", "Michoacán de Ocampo": "Michoacán", "Veracruz de Ignacio de la Llave": "Veracruz", "Coahuila de Zaragoza": "Coahuila" };
  const nm = (f) => alias[f.properties.nomgeo] || f.properties.nomgeo;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="portafolio-kpis">
        {[["Cartera dispuesta en sistema", money(d.total)], ["Cartera total (parámetro)", money(d.cartera_total)], ["Reservas estimadas", money(d.reservas)], ["Créditos", d.deals.length]].map(([l, v]) => (
          <div key={l} className="rounded-2xl border border-[#E2DDD5] bg-white p-5"><p className="text-[11px] font-semibold uppercase tracking-widest text-[#8C867A]">{l}</p><p className="font-serif-display mt-1 text-2xl font-bold">{v}</p></div>))}
      </div>
      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-7" title="Concentración por estado" subtitle={`% de la cartera total · límite ${d.limites.estado}%`} testid="portafolio-mapa">
          <div className="h-[380px] overflow-hidden rounded-2xl border border-[#E2DDD5]">
            <MapContainer center={[23.6, -102.5]} zoom={5} scrollWheelZoom={false} style={{ height: "100%", background: "#F7F5F1" }}>
              <TileLayer url={TILE} attribution={ATTR} /><FitTo data={geo} />
              <GeoJSON data={geo} style={(f) => ({ fillColor: pct(nm(f)) ? colorIdx(Math.min(100, (pct(nm(f)) / d.limites.estado) * 75)) : "#EEEAE3", weight: 0.8, color: "#fff", fillOpacity: 0.9 })}
                onEachFeature={(f, l) => l.bindTooltip(`<b>${nm(f)}</b><br/>${pct(nm(f)) ? `${pct(nm(f)).toFixed(2)}% de la cartera · ${money(byEst[nm(f)].monto)}` : "Sin exposición"}`, { sticky: true })} />
            </MapContainer></div>
          <div className="mt-3"><Legend /></div>
        </Card>
        <Card className="lg:col-span-5" title="Por estado" testid="portafolio-estado"><Bars rows={d.por_estado} lim={d.limites.estado} testid="bars-estado" /></Card>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Por sector" subtitle={`Límite ${d.limites.sector}%`} testid="portafolio-sector"><Bars rows={d.por_sector} lim={d.limites.sector} testid="bars-sector" /></Card>
        <Card title="Por banda de score" testid="portafolio-banda"><Bars rows={d.por_banda} testid="bars-banda" /></Card>
        <Card title="Por tipo de garantía" testid="portafolio-garantia"><Bars rows={d.por_garantia} testid="bars-garantia" /></Card>
      </div>
    </div>
  );
};
